import {
	COMPANY_MANAGED_PROJECT,
	TEAM_MANAGED_PROJECT,
	type ProjectType,
	type JiraProjectStyle,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import type {
	ProjectContext,
	ProjectIssueType as IssueType,
} from '@atlassian/jira-issue-shared-types/src/common/types/project-type.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createHook, createStore } from '@atlassian/react-sweet-state';
import * as actions from './actions.tsx';
import type { State, SelectorProps } from './types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initialState: Record<string, any> = {};
type Actions = typeof actions;

export const store = createStore<State, Actions>({
	name: 'project-context',
	initialState,
	actions,
});

export const useProjectContextActionStore = createHook(store, {
	selector: null,
});

const getProjectContext = (
	state: State,
	{ projectKey }: SelectorProps,
): ProjectContext | undefined => state[projectKey];
export const useProjectContextStore = createHook(store, {
	selector: getProjectContext,
});

const getProjectType = (state: State, { projectKey }: SelectorProps): ProjectType | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.projectType;
};

const getJiraProjectStyle = (
	state: State,
	{ projectKey }: SelectorProps,
): JiraProjectStyle | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.isSimplified ? TEAM_MANAGED_PROJECT : COMPANY_MANAGED_PROJECT;
};

export const useProjectContextProjectTypeStore = createHook(store, {
	selector: getProjectType,
});

export const useProjectContextJiraProjectStyleStore = createHook(store, {
	selector: getJiraProjectStyle,
});

const getProjectName = (state: State, { projectKey }: SelectorProps): string | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.projectName;
};

export const useProjectContextProjectNameStore = createHook(store, {
	selector: getProjectName,
});

const getIssueTypes = (state: State, { projectKey }: SelectorProps): IssueType[] | undefined => {
	const projectContext = getProjectContext(state, { projectKey });
	return projectContext?.issueTypes;
};

export const useProjectContextIssueTypesStore = createHook(store, {
	selector: getIssueTypes,
});
